'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import { connect } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { once } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIBox from 'UIComponents/layout/UIBox';
import { triggerWootricSurvey } from '../utils/triggerWootricSurvey.js';
import { FilePropTypes } from 'DesignData/propTypes/finder';
import CustomWidgetBuffer from 'DesignData/models/CustomWidgetBuffer';
import CustomWidgetEditor from 'DesignEditorsUI/components/customWidgetEditor/CustomWidgetEditor';
import * as CustomWidgetBufferActions from 'DesignData/actions/CustomWidgetBuffers';
import * as CustomWidgetEditorOptionsActions from 'DesignData/actions/CustomWidgetEditorOptions';
import * as ApplicationActions from 'DesignData/actions/Application';
import { guard } from 'DesignData/lib/functions';
import { getUserHasReadAccessOnly } from 'DesignData/selectors/auth';
import * as previewOptionsSelectors from 'DesignData/selectors/previewOptions';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import * as customWidgetEditorOptionsSelectors from 'DesignData/selectors/customWidgetEditorOptions';
import * as appSelectors from 'DesignData/selectors/app';
import { getFile, getIsProjectsFile } from 'DesignData/selectors/finder';
import { trackDefaultModuleOpen } from '../utils/usageTracker/eventUtils';
import { compose } from 'redux';
const fetchCustomWidgetEditorOptionsOnce = once(fetchCustomWidgetEditorOptions => {
  fetchCustomWidgetEditorOptions();
});

class CustomWidgetEditorContainer extends Component {
  constructor(...args) {
    super(...args);

    this.guardReadOnly = () => {
      return this.props.isReadOnly || this.props.isProjectsFile;
    };

    this.bindAndGuardReadOnly = guard.bind(null, this.guardReadOnly);
    this.onSave = this.bindAndGuardReadOnly(() => {
      const {
        customWidgetId,
        saveCustomWidgetBuffer,
        requestStatus
      } = this.props;

      if (!requestStatus.isPublishing) {
        saveCustomWidgetBuffer(customWidgetId);
      }
    });
    this.onRedoCustomWidgetEdit = this.bindAndGuardReadOnly(() => {
      const {
        customWidgetId,
        redoCustomWidgetEdit
      } = this.props;
      redoCustomWidgetEdit(customWidgetId);
    });
    this.onUndoCustomWidgetEdit = this.bindAndGuardReadOnly(() => {
      const {
        customWidgetId,
        undoCustomWidgetEdit
      } = this.props;
      undoCustomWidgetEdit(customWidgetId);
    });
    this.publishCustomWidget = this.bindAndGuardReadOnly(() => {
      const {
        customWidgetId,
        publishCustomWidgetBuffer
      } = this.props;
      publishCustomWidgetBuffer(customWidgetId);
    });

    this.updatePaneOptions = options => {
      const {
        customWidgetId,
        updatePaneOptions
      } = this.props;
      updatePaneOptions(customWidgetId, options);
    };
  }

  componentDidMount() {
    const {
      isReadOnly,
      customWidgetBuffer
    } = this.props;
    if (!this.props.customWidgetId) return;
    this.validateOnInitialLoad();

    if (this.props.revisionId) {
      this.props.fetchCustomWidgetRevision(this.props.customWidgetId, this.props.revisionId);
    } else {
      this.props.fetchCustomWidgetBuffer(this.props.customWidgetId);
    }

    fetchCustomWidgetEditorOptionsOnce(this.props.fetchCustomWidgetEditorOptions);
    this.props.hydrateCustomWidgetEditorOptions(this.props.customWidgetId);

    if (customWidgetBuffer && isReadOnly) {
      this.trackDefaultModuleOpen();
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.customWidgetId) return;

    if (this.props.revisionId && prevProps.revisionId !== this.props.revisionId) {
      this.props.fetchCustomWidgetRevision(this.props.customWidgetId, this.props.revisionId);
    } else if (prevProps.customWidgetId !== this.props.customWidgetId || prevProps.revisionId && !this.props.revisionId) {
      this.props.fetchCustomWidgetBuffer(this.props.customWidgetId);
    }

    this.validateOnInitialLoad();
    const {
      customWidgetId,
      isReadOnly,
      customWidgetBuffer
    } = this.props;

    if (prevProps.customWidgetId !== customWidgetId) {
      this.props.hydrateCustomWidgetEditorOptions(customWidgetId);
    }

    if (!prevProps.customWidgetBuffer && customWidgetBuffer) {
      if (isReadOnly) {
        this.trackDefaultModuleOpen();
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (!nextProps.customWidgetId) return false;
    return shallowCompare(this, nextProps, nextState);
  }

  trackDefaultModuleOpen() {
    const {
      customWidgetBuffer
    } = this.props;

    if (customWidgetBuffer.default) {
      trackDefaultModuleOpen(customWidgetBuffer.path);
    }
  }

  validateOnInitialLoad() {
    if (this.props.isReadOnly || this.props.revisionId || this.props.isProjectsFile) {
      return;
    }

    const {
      customWidgetId
    } = this.props;

    if (!customWidgetId) {
      return;
    }

    const {
      buffersInitiallyValidated
    } = CustomWidgetEditorContainer;

    if (buffersInitiallyValidated[customWidgetId]) {
      return;
    }

    this.props.validateCustomWidgetBuffer(customWidgetId);
    buffersInitiallyValidated[customWidgetId] = true;
  }

  render() {
    const {
      customWidgetBuffer,
      customWidgetId,
      isPreviewOpen,
      paneOptions,
      revisionId,
      isEditingGlobalContent,
      isFullScreenEditorOpen,
      selectedLanguageEditor,
      folderPath,
      fileName,
      requestStatus,
      editorLoaded,
      isProjectsFile
    } = this.props;

    if (requestStatus.fetchError) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "MODULE_EDITOR_ERROR",
        children: /*#__PURE__*/_jsx(UIBox, {
          grow: 1,
          children: /*#__PURE__*/_jsx(UIErrorMessage, {
            title: unescapedText('designManager.errorPages.loadingErrors.moduleEditor.title'),
            type: "badRequest",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "designManager.errorPages.loadingErrors.moduleEditor.message"
            })
          })
        })
      });
    }

    if (!customWidgetBuffer) {
      return /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true,
        minHeight: 300
      });
    }

    triggerWootricSurvey();
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "MODULE_EDITOR",
      children: /*#__PURE__*/_jsx(CustomWidgetEditor, {
        customWidgetId: customWidgetId,
        revisionId: revisionId,
        isPreviewOpen: isPreviewOpen,
        isEditingGlobalContent: isEditingGlobalContent,
        isFullScreenEditorOpen: isFullScreenEditorOpen,
        onPublish: this.onPublish,
        onRedoCustomWidgetEdit: this.onRedoCustomWidgetEdit,
        onSave: this.onSave,
        onUndoCustomWidgetEdit: this.onUndoCustomWidgetEdit,
        onPublishCustomWidget: this.publishCustomWidget,
        paneOptions: paneOptions,
        updatePaneOptions: this.updatePaneOptions,
        selectedLanguageEditor: selectedLanguageEditor,
        fileName: fileName,
        folderPath: folderPath,
        editorLoaded: editorLoaded,
        isProjectsFile: isProjectsFile
      })
    });
  }

}

CustomWidgetEditorContainer.propTypes = {
  customWidgetBuffer: PropTypes.instanceOf(CustomWidgetBuffer),
  customWidgetId: CustomWidgetPropTypes.customWidgetId,
  revisionId: PropTypes.number,
  fetchCustomWidgetBuffer: PropTypes.func.isRequired,
  fetchCustomWidgetRevision: PropTypes.func.isRequired,
  fetchCustomWidgetEditorOptions: PropTypes.func.isRequired,
  hydrateCustomWidgetEditorOptions: PropTypes.func.isRequired,
  isPreviewOpen: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  isProjectsFile: PropTypes.bool,
  isEditingGlobalContent: PropTypes.bool.isRequired,
  paneOptions: PropTypes.object.isRequired,
  redoCustomWidgetEdit: PropTypes.func.isRequired,
  requestStatus: PropTypes.object.isRequired,
  saveCustomWidgetBuffer: PropTypes.func.isRequired,
  undoCustomWidgetEdit: PropTypes.func.isRequired,
  updatePaneOptions: PropTypes.func.isRequired,
  validateCustomWidgetBuffer: PropTypes.func.isRequired,
  isFullScreenEditorOpen: PropTypes.bool.isRequired,
  selectedLanguageEditor: PropTypes.string,
  publishCustomWidgetBuffer: PropTypes.func.isRequired,
  fileName: FilePropTypes.fileName,
  folderPath: FilePropTypes.folderPath,
  editorLoaded: PropTypes.func.isRequired
};
CustomWidgetEditorContainer.defaultProps = {
  customWidgetBuffer: null
};
CustomWidgetEditorContainer.buffersInitiallyValidated = {};

function makeMapStateToProps() {
  const getRequestStatus = customWidgetBuffersSelectors.makeGetRequestStatus();
  const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
  const getIsReadOnly = customWidgetBuffersSelectors.makeGetIsReadOnly();
  return (state, props) => {
    const customWidgetId = props.assetId || appSelectors.getCustomWidgetId(state);

    const __props = Object.assign({}, props, {
      customWidgetId
    });

    const file = getFile(state, {
      fileId: customWidgetId
    });
    return {
      customWidgetId,
      customWidgetBuffer: getCustomWidgetBuffer(state, __props),
      isPreviewOpen: previewOptionsSelectors.getIsPreviewOpen(state),
      isReadOnly: getUserHasReadAccessOnly(state) || getIsReadOnly(state, __props),
      paneOptions: customWidgetEditorOptionsSelectors.getPaneOptions(state, __props),
      isProjectsFile: getIsProjectsFile(state, {
        fileId: customWidgetId
      }),
      requestStatus: getRequestStatus(state, __props),
      isEditingGlobalContent: customWidgetEditorOptionsSelectors.getIsEditingGlobalContent(state, __props),
      isFullScreenEditorOpen: customWidgetEditorOptionsSelectors.getIsFullScreenEditorOpen(state, __props),
      selectedLanguageEditor: customWidgetEditorOptionsSelectors.getSelectedLanguageEditor(state, __props),
      folderPath: file ? file.folderPath : '',
      fileName: file ? file.name : ''
    };
  };
}

const mapDispatchToProps = {
  fetchCustomWidgetRevision: CustomWidgetBufferActions.fetchRevision,
  fetchCustomWidgetEditorOptions: CustomWidgetEditorOptionsActions.fetch,
  hydrateCustomWidgetEditorOptions: CustomWidgetEditorOptionsActions.hydrate,
  redoCustomWidgetEdit: CustomWidgetBufferActions.redoEdit,
  saveCustomWidgetBuffer: CustomWidgetBufferActions.save,
  updatePaneOptions: CustomWidgetEditorOptionsActions.updatePaneOptions,
  undoCustomWidgetEdit: CustomWidgetBufferActions.undoEdit,
  validateCustomWidgetBuffer: CustomWidgetBufferActions.validate,
  publishCustomWidgetBuffer: CustomWidgetBufferActions.publish,
  editorLoaded: ApplicationActions.editorLoaded
};
export default compose(connect(makeMapStateToProps, mapDispatchToProps))(CustomWidgetEditorContainer);